"use client";

import { useState, useEffect } from "react";
import {  useNavigate, useParams  } from "react-router-dom";
import { ArrowLeft, Menu } from "lucide-react";
import * as api from "@/src/api/sdk.gen";
import Markdown from "@/components/Markdown";
import { useQuery } from "@tanstack/react-query";
import { AccessError } from "@/components/student/AccessError";
import RemedialLessonContent, {
    type RemedialLessonData,
    type ProfessionalRemedialLessonData,
    isStructuredRemedialData,
    isProfessionalRemedialData,
} from "@/components/student/RemedialLessonContent";
import {
    lessonContentDirection,
    normalizeLessonLanguage,
    type LessonLanguage,
} from "@/lib/lesson-language";
import LegacySectionsLessonContent, { isLegacySectionsData } from "@/components/student/LegacySectionsLessonContent";

interface RemedialLesson {
    id: number;
    uuid: string;
    generated_text: string;
    structured_content?: RemedialLessonData | ProfessionalRemedialLessonData | null;
    original_lesson_id: number;
    lessons: {
        title: string;
    };
    userDomain?: string | null;
    language?: LessonLanguage;
    cacheFetchedAt?: number;
}

const remedialCache = new Map<string, RemedialLesson>();
const remedialStorageKey = (id: string) => `remedial-lesson-cache:${id}`;
const REMEDIAL_CACHE_TTL_MS = 5 * 60 * 1000;
const MAX_REMEDIAL_CACHE_ITEMS = 50;

function getRemedialCache(id: string): RemedialLesson | null {
    const v = remedialCache.get(id) ?? null;
    if (!v) return null;
    // Touch order for LRU behavior.
    remedialCache.delete(id);
    remedialCache.set(id, v);
    return v;
}

function setRemedialCache(id: string, value: RemedialLesson) {
    if (remedialCache.has(id)) {
        remedialCache.delete(id);
    }
    remedialCache.set(id, value);
    if (remedialCache.size > MAX_REMEDIAL_CACHE_ITEMS) {
        const oldestKey = remedialCache.keys().next().value as string | undefined;
        if (oldestKey) remedialCache.delete(oldestKey);
    }
}

export default function RemedialLessonPage() {
    const params = useParams();
    const id = params?.id as string | undefined;
    const navigate = useNavigate();
    const getCachedLesson = (lessonId: string): RemedialLesson | null => {
        const memory = getRemedialCache(lessonId);
        if (memory) return memory;
        if (typeof window !== "undefined") {
            try {
                const raw = window.sessionStorage.getItem(remedialStorageKey(lessonId));
                if (raw) {
                    const parsed = JSON.parse(raw) as RemedialLesson;
                    setRemedialCache(lessonId, parsed);
                    return parsed;
                }
            } catch {}
        }
        return null;
    };

    const [hydratedCachedLesson, setHydratedCachedLesson] = useState<RemedialLesson | null>(() => {
        if (!id) return null;
        return getCachedLesson(id);
    });

    useEffect(() => {
        if (!id) return;
        const syncTimer = setTimeout(() => setHydratedCachedLesson(getCachedLesson(id)), 0);
        return () => clearTimeout(syncTimer);
    }, [id]);

    const remedialQuery = useQuery({
        queryKey: ["remedial-lesson-payload", id],
        enabled: !!id,
        initialData: id ? hydratedCachedLesson ?? undefined : undefined,
        staleTime: REMEDIAL_CACHE_TTL_MS,
        refetchOnMount: true,
        queryFn: async () => {
            try {
                const { data, error, response } = await api.getRemedialLessonContentApiV1RemedialLessonContentUuidGet({
                    path: { uuid: id as string },
                });
                if (response?.status === 403) throw new Error("forbidden");
                if (response?.status === 404) throw new Error("notFound");
                if (error) throw new Error("error");
                const stamped: RemedialLesson = {
                    ...(data as RemedialLesson),
                    cacheFetchedAt: Date.now(),
                };
                setRemedialCache(id as string, stamped);
                if (typeof window !== "undefined") {
                    try {
                        window.sessionStorage.setItem(remedialStorageKey(id as string), JSON.stringify(stamped));
                    } catch {}
                }
                return stamped;
            } catch (err: any) {
                if (err?.message === "forbidden" || err?.message === "notFound" || err?.message === "error") throw err;
                throw new Error("error");
            }
        },
    });

    const lesson = remedialQuery.data ?? hydratedCachedLesson;
    const loading = remedialQuery.isLoading && !lesson;
    const errorKind = remedialQuery.error ? (remedialQuery.error as Error).message : null;

    if (!id) return <div className="min-h-screen flex items-center justify-center font-montserrat">Invalid lesson.</div>;
    if (errorKind === "forbidden" && !lesson) {
        return (
            <AccessError
                title="Lesson not available"
                message="This remedial lesson isn't available. It may belong to another account or no longer be accessible."
                primaryHref="/courses"
                primaryLabel="Back to courses"
            />
        );
    }
    if (errorKind === "notFound" && !lesson) {
        return (
            <AccessError
                title="Lesson not found"
                message="This remedial lesson link appears invalid or no longer available."
                primaryHref="/courses"
                primaryLabel="Back to courses"
            />
        );
    }
    if (errorKind === "error" && !lesson) {
        return (
            <AccessError
                title="Something went wrong"
                message="We couldn't load this remedial lesson. Please try again."
                primaryHref="/courses"
                primaryLabel="Back to courses"
            />
        );
    }
    if (!lesson && loading) {
        return (
            <div className="min-h-screen flex items-center justify-center font-montserrat text-slate-600">
                Loading remedial lesson...
            </div>
        );
    }
    if (!lesson) return <div className="min-h-screen flex items-center justify-center font-montserrat">Lesson not found.</div>;

    const lessonTitle = lesson.lessons?.title ?? "Remedial lesson";
    const lessonLanguage = normalizeLessonLanguage(lesson.language);
    const contentDirection = lessonContentDirection(lessonLanguage);

    return (
        <div className="min-h-[calc(100vh-64px)] bg-gray-50 flex flex-col font-montserrat">
            {/* Nav – same structure as regular lesson; remedial = amber icon */}
            <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                        <button
                            className="lg:hidden p-1.5 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors flex-shrink-0"
                            onClick={() => typeof window !== "undefined" && window.dispatchEvent(new Event("open-student-sidebar"))}
                            aria-label="Toggle navigation"
                        >
                            <Menu className="w-5 h-5" />
                        </button>
                        <button
                            onClick={() => navigate(-1)}
                            className="w-9 h-9 rounded-lg flex items-center justify-center text-white flex-shrink-0 bg-amber-600 hover:bg-amber-700 transition-colors"
                            aria-label="Back to course overview"
                        >
                            <ArrowLeft className="w-5 h-5" />
                        </button>
                        <div className="min-w-0">
                            <p className="text-[9px] font-black text-amber-600 uppercase tracking-[0.3em] leading-tight">Remedial Support</p>
                            <h1 className="text-base font-bold text-slate-800 flex items-center gap-2 min-w-0">
                                <span className="truncate">{lessonTitle}</span>
                                <span
                                    className="shrink-0 rounded-md bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600"
                                    title="Lesson language"
                                >
                                    {lessonLanguage}
                                </span>
                            </h1>
                        </div>
                    </div>
                    <div className="hidden sm:flex flex-col items-end flex-shrink-0">
                        <span className="text-[9px] text-slate-400 font-black uppercase tracking-widest">Industry focus</span>
                        <span className="text-xs font-bold text-slate-600">{lesson.userDomain ?? "—"}</span>
                    </div>
                </div>
            </nav>

            {/* Content – same padding as regular lesson */}
            <main className="flex-1 overflow-y-auto px-4 sm:px-6 lg:px-10 pt-6 pb-4 w-full max-w-5xl mx-auto">
                <div className="max-w-none mx-auto w-full">
                    <article
                        className="learning-content max-w-none prose prose-slate prose-p:text-gray-700 prose-li:text-gray-700"
                        dir={contentDirection}
                        lang={lessonLanguage === "Arabic" ? "ar" : "en"}
                    >
                        {loading ? (
                            <div className="space-y-3 not-prose">
                                <div className="h-4 w-40 animate-pulse rounded bg-slate-200" />
                                <div className="h-4 w-full animate-pulse rounded bg-slate-200/80" />
                                <div className="h-4 w-11/12 animate-pulse rounded bg-slate-200/70" />
                                <div className="h-4 w-3/4 animate-pulse rounded bg-slate-200/60" />
                            </div>
                        ) : (isStructuredRemedialData(lesson.structured_content) || isProfessionalRemedialData(lesson.structured_content)) ? (
                            <RemedialLessonContent data={lesson.structured_content} />
                        ) : isLegacySectionsData(lesson.structured_content) ? (
                            <LegacySectionsLessonContent data={lesson.structured_content} />
                        ) : (
                            <>
                                <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 not-prose">
                                    Content format issue. Showing raw content.
                                </div>
                                <Markdown content={lesson.generated_text} />
                            </>
                        )}
                    </article>
                </div>
            </main>

            {/* Footer / Action – same padding as regular lesson bottom bar */}
            <footer className="bg-white border-t border-gray-200 px-3 py-3.5 sm:px-4 sm:py-3.5 lg:px-6 sticky bottom-0 z-20 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]">
                <div className="max-w-5xl mx-auto w-full flex items-center justify-between">
                    <div>
                        <p className="text-sm text-gray-500">
                            You can continue from your course overview.
                        </p>
                    </div>
                    <button
                        onClick={() => navigate(-1)}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-lg font-bold text-sm transition-colors flex items-center gap-2 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                    >
                        Back to course
                    </button>
                </div>
            </footer>
        </div>
    );
}
